import { useCallback, useReducer, type ChangeEvent } from 'react';
import type { SortDirection } from 'types/call.type';
import type { SMSSortField } from 'types/sms.type';
import type { DateRangeType } from '../../AgenticWorkflowDetail.types';
import { getDateRangeForRangeType } from '../../utils';
import type { SMSAnalyticsFilterName } from '../../components/SMSAnalytics';
import type { SMSLead } from '../../components/SMSTable';

export type SMSAnalyticsState = {
    dateRange: [Date | null, Date | null];
    dateRangeType: DateRangeType;
    filterLeadStatus: string;
    filterSMSStatus: string;
    filterSMSDirection: string;
    smsSortField: SMSSortField;
    sortDirection: SortDirection;
    selectedIds: string[];
    searchTerm: string;
    currentPage: number;
    rowsPerPage: number;
};

type SMSAnalyticsAction =
    | { type: 'setSearch'; value: string }
    | { type: 'setDateRange'; dateRange: [Date | null, Date | null] }
    | { type: 'setDateRangeType'; range: string }
    | { type: 'setFilter'; filter: SMSAnalyticsFilterName; value: string }
    | { type: 'setSort'; field: SMSSortField; direction: SortDirection }
    | { type: 'setPage'; page: number }
    | { type: 'setRowsPerPage'; rowsPerPage: number }
    | { type: 'syncPagination'; currentPage?: number; perPage?: number }
    | { type: 'toggleSelection'; conversationId: string }
    | { type: 'selectPage'; conversationIds: string[] }
    | { type: 'deselectPage'; conversationIds: string[] }
    | { type: 'clearSelection' }
    | { type: 'clearFilters' };

const createInitialState = (initialStartDate: Date, initialEndDate: Date): SMSAnalyticsState => ({
    dateRange: [initialStartDate, initialEndDate],
    dateRangeType: 'today',
    filterLeadStatus: '',
    filterSMSStatus: '',
    filterSMSDirection: '',
    smsSortField: 'smsDate',
    sortDirection: 'asc',
    selectedIds: [],
    searchTerm: '',
    currentPage: 1,
    rowsPerPage: 10,
});

const resetPage = { currentPage: 1 } as const;

const smsAnalyticsReducer = (state: SMSAnalyticsState, action: SMSAnalyticsAction): SMSAnalyticsState => {
    switch (action.type) {
        case 'setSearch':
            return { ...state, searchTerm: action.value, ...resetPage };
        case 'setDateRange':
            return { ...state, dateRange: action.dateRange, ...resetPage };
        case 'setDateRangeType': {
            const nextRange = getDateRangeForRangeType(action.range);
            return {
                ...state,
                dateRangeType: action.range as DateRangeType,
                ...(nextRange ? { dateRange: nextRange, ...resetPage } : {}),
            };
        }
        case 'setFilter': {
            const next =
                action.filter === 'leadStatus'
                    ? { filterLeadStatus: action.value }
                    : action.filter === 'smsStatus'
                      ? { filterSMSStatus: action.value }
                      : { filterSMSDirection: action.value };
            return { ...state, ...next, ...resetPage };
        }
        case 'setSort':
            return { ...state, smsSortField: action.field, sortDirection: action.direction };
        case 'setPage':
            return { ...state, currentPage: action.page };
        case 'setRowsPerPage':
            return { ...state, rowsPerPage: action.rowsPerPage, ...resetPage };
        case 'syncPagination':
            return {
                ...state,
                ...(action.currentPage != null ? { currentPage: action.currentPage } : {}),
                ...(action.perPage != null ? { rowsPerPage: action.perPage } : {}),
            };
        case 'toggleSelection':
            return {
                ...state,
                selectedIds: state.selectedIds.includes(action.conversationId)
                    ? state.selectedIds.filter((id) => id !== action.conversationId)
                    : [...state.selectedIds, action.conversationId],
            };
        case 'selectPage':
            return {
                ...state,
                selectedIds: Array.from(new Set([...state.selectedIds, ...action.conversationIds])),
            };
        case 'deselectPage':
            return {
                ...state,
                selectedIds: state.selectedIds.filter((id) => !action.conversationIds.includes(id)),
            };
        case 'clearSelection':
            return { ...state, selectedIds: [] };
        case 'clearFilters':
            return {
                ...state,
                searchTerm: '',
                filterLeadStatus: '',
                filterSMSStatus: '',
                filterSMSDirection: '',
                dateRangeType: 'today',
                dateRange: [new Date(), new Date()],
                selectedIds: [],
                ...resetPage,
            };
        default:
            return state;
    }
};

export const useSMSAnalyticsState = (initialStartDate: Date, initialEndDate: Date) => {
    const [state, dispatch] = useReducer(smsAnalyticsReducer, undefined, () =>
        createInitialState(initialStartDate, initialEndDate)
    );

    const handleSearchChange = useCallback((value: string) => {
        if (value.length > 1 || value.length === 0) {
            dispatch({ type: 'setSearch', value });
        }
    }, []);

    const handleSetDateRange = useCallback((dateRange: [Date | null, Date | null]) => {
        dispatch({ type: 'setDateRange', dateRange });
    }, []);

    const handleDateRangeTypeChange = useCallback((range: string) => {
        dispatch({ type: 'setDateRangeType', range });
    }, []);

    const handleFilterChange = useCallback((filter: SMSAnalyticsFilterName, value: string) => {
        dispatch({ type: 'setFilter', filter, value });
    }, []);

    const handleSortChange = useCallback((field: SMSSortField, direction: SortDirection) => {
        dispatch({ type: 'setSort', field, direction });
    }, []);

    const handlePageChange = useCallback((page: number) => {
        dispatch({ type: 'setPage', page });
    }, []);

    const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
        dispatch({ type: 'setRowsPerPage', rowsPerPage });
    }, []);

    const syncPagination = useCallback((pagination?: { currentPage?: number; perPage?: number }) => {
        if (!pagination) return;
        dispatch({
            type: 'syncPagination',
            currentPage: pagination.currentPage,
            perPage: pagination.perPage,
        });
    }, []);

    const clampPageToTotal = useCallback((currentPage: number, totalPages: number) => {
        if (totalPages > 0 && currentPage > totalPages) {
            dispatch({ type: 'setPage', page: totalPages });
        }
    }, []);

    const handleCheckboxChange = useCallback((conversationId: string) => {
        dispatch({ type: 'toggleSelection', conversationId });
    }, []);

    const handleSelectAllCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>, pageLeads: SMSLead[]) => {
        event.stopPropagation();
        const conversationIds = pageLeads.map((sms) => sms.conversationId);
        if (event.target.checked) {
            dispatch({ type: 'selectPage', conversationIds });
        } else {
            dispatch({ type: 'deselectPage', conversationIds });
        }
    }, []);

    const clearSelection = useCallback(() => {
        dispatch({ type: 'clearSelection' });
    }, []);

    const handleClearFilters = useCallback(() => {
        dispatch({ type: 'clearFilters' });
    }, []);

    return {
        state,
        handleSearchChange,
        handleSetDateRange,
        handleDateRangeTypeChange,
        handleFilterChange,
        handleSortChange,
        handlePageChange,
        handleRowsPerPageChange,
        syncPagination,
        clampPageToTotal,
        handleCheckboxChange,
        handleSelectAllCheckboxChange,
        clearSelection,
        handleClearFilters,
    };
};
